/**
 * P3 SP1 — the pure lens-visibility predicate. The mode×subject matrix in
 * the test next door IS the spec (rev 1.1 §3). No React, no api.
 * Branches on state presence (activeTrailId/set), not on `mode`, so a
 * future trail×persona compose is additive.
 */
import type { LensMode } from '../types/council';

export interface LensSnapshot {
  mode: LensMode;
  activePersonaIds: Set<string>;
  activeTrailId: string | null;
  defaultTrailId: string | null; // session metadata — composed in, NEVER serialized with lens state
}

export interface LensSubject {
  kind: 'operator' | 'peer';
  personaId?: string;
  trailId?: string | null;
}

export function lensVisibility(snap: LensSnapshot, subj: LensSubject): 'on' | 'off' {
  if (snap.mode === 'trail') {
    if (!snap.activeTrailId) return 'on'; // degenerate
    const t = subj.trailId ?? null;
    if (t === snap.activeTrailId) return 'on';
    // legacy-null: pre-minting messages belong to the default trail
    if (t === null && snap.activeTrailId === snap.defaultTrailId) return 'on';
    return 'off';
  }
  if (subj.kind === 'operator') return 'on';
  if (snap.activePersonaIds.size === 0) return 'on'; // degenerate canopy
  return subj.personaId && snap.activePersonaIds.has(subj.personaId) ? 'on' : 'off';
}

/** Display mode is a DERIVATION of state — trail (sticky) wins, else set size. */
export function deriveMode(activePersonaIds: Set<string>, activeTrailId: string | null): LensMode {
  if (activeTrailId) return 'trail';
  if (activePersonaIds.size === 0) return 'canopy';
  return activePersonaIds.size === 1 ? 'single' : 'multi';
}
